import React, { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, DollarSign, ChevronDown, ChevronUp } from "lucide-react";
import { FEE_STATUS_META } from "./feeUtils";
import { FeeDetailFields } from "./FeeDetailFields";
import { FeeAttachments } from "./FeeAttachments";

export const FeeAddForm = ({ form, setForm, onCreate, saving, project = {}, projectId }) => {
  const [showMore, setShowMore] = useState(false);
  const detailCount = [form.paid_by, form.invoiced_to_type].filter(Boolean).length + (form.attachments?.length || 0);

  return (
    <div className="border border-zinc-200 bg-white p-4 mb-6">
      <div className="flex items-center gap-2 mb-3 text-xs font-semibold uppercase tracking-wider text-zinc-500">
        <DollarSign className="h-3.5 w-3.5" /> Add a fee
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_140px_150px_150px_auto] gap-2 items-center">
        <Input data-testid="fee-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="e.g. DA lodgement fee" className="rounded-sm" />
        <Input data-testid="fee-amount-input" type="number" min="0" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="Amount (AUD)" className="rounded-sm" />
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger className="rounded-sm" data-testid="fee-status-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.keys(FEE_STATUS_META).map((s) => <SelectItem key={s} value={s}>{FEE_STATUS_META[s].label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="fee-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm" />
        <Button data-testid="fee-add-button" onClick={onCreate} disabled={saving} className="bg-zinc-900 hover:bg-zinc-800 rounded-sm gap-1">
          <Plus className="h-4 w-4" /> Add
        </Button>
      </div>
      <div className="flex flex-wrap items-center gap-4 mt-2">
        <Input data-testid="fee-note-input" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Optional note…" className="rounded-sm flex-1 min-w-[200px]" />
        <Input data-testid="fee-invoice-input" value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} placeholder="Invoice #" className="rounded-sm w-[160px]" />
        <label className="flex items-center gap-2 text-sm text-zinc-600 cursor-pointer" data-testid="fee-gst-toggle">
          <input type="checkbox" checked={form.gst} onChange={(e) => setForm({ ...form, gst: e.target.checked })} className="h-4 w-4 rounded-sm accent-zinc-900" />
          Add 10% GST
        </label>
      </div>

      <button type="button" onClick={() => setShowMore((s) => !s)}
        className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-600 hover:text-zinc-900 transition-colors" data-testid="fee-more-toggle">
        {showMore ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
        Payment & invoicing details{!showMore && detailCount > 0 ? ` (${detailCount})` : ""}
      </button>

      {showMore && (
        <div className="mt-3 pt-3 border-t border-zinc-100 space-y-3">
          <FeeDetailFields value={form} onChange={setForm} project={project} />
          <FeeAttachments attachments={form.attachments || []} onChange={(fn) => setForm((prev) => ({ ...prev, attachments: fn(prev.attachments || []) }))} projectId={projectId} />
        </div>
      )}
    </div>
  );
};
